/**
 * ML Trading Page — Multi-Algorithm Dashboard
 *
 * Shows active opportunities across all tickers and algorithms (V1, V2, etc).
 * Scales to N algorithms without layout changes.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import useIntradayML from '../hooks/useIntradayML.js';
import useAuth from '../hooks/useAuth.jsx';
import LoginModal from '../components/Auth/LoginModal.jsx';
import SubscriptionWall from '../components/Auth/SubscriptionWall.jsx';
import './IntradayML.css';

const YEARS = ['All', '2026', '2025', '2024', '2023', '2022', '2021'];

// ── Tooltip Component (mobile-friendly: tap to toggle) ──────────────────────

const TOOLTIPS = {
  confidence: 'Model probability of hitting the profit target before the stop loss. Not a win probability — the actual win rate depends on the risk-reward ratio.',
  profit_factor: 'Total gross profits divided by total gross losses. PF > 1 = profitable. PF > 2 = strong. PF > 3 = excellent.',
  win_rate: 'Percentage of trades that closed with a positive P&L. Higher is better, but a lower win rate can still be profitable with a high risk-reward ratio.',
  cagr: 'Compound Annual Growth Rate — annualized return of the strategy over the backtest period.',
  max_dd: 'Maximum Drawdown — the largest peak-to-trough decline during the backtest. Lower (closer to 0) is better.',
  signal: 'BUY = model recommends entry. WAIT = conditions close but not met. NO TRADE = no opportunity detected.',
  stop: 'Price level where the position will be automatically closed to limit losses.',
  target: 'Price level where the position will be automatically closed to lock in profits.',
};

function Tooltip({ id, children }) {
  const [open, setOpen] = useState(false);
  const text = TOOLTIPS[id];
  if (!text) return children;
  return (
    <span className="tooltip-wrap" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
      {children}<span className="tooltip-icon">?</span>
      {open && <span className="tooltip-bubble">{text}</span>}
    </span>
  );
}

// Algorithm display config — add new algorithms here
// Clean models only — leaked V1/V2 removed
const ALGO_CONFIG = {
  v1c: { label: 'ML V1C', color: '#34d399', cssClass: 'v1c', hidden: false },
  v2c: { label: 'ML V2C', color: '#f472b6', cssClass: 'v2c', hidden: false },
};

// V2 regime tier config — ordered from loosest to tightest
const REGIME_TIERS = {
  none:     { label: 'No Filter', short: 'No Filter', desc: 'No market condition filter' },
  relaxed:  { label: 'Relaxed',   short: 'Relaxed',   desc: 'VIX<40, RSI 25-85' },
  moderate: { label: 'Moderate',  short: 'Moderate',   desc: 'VIX<35, RSI 30-80, SMA50>-15%' },
  firm:     { label: 'Firm',      short: 'Firm',       desc: 'VIX<32, RSI 35-78, SMA50>-10%' },
  strict:   { label: 'Strict',    short: 'Strict',     desc: 'VIX<30, RSI 40-75, price>SMA50' },
};
const ALL_REGIME_KEYS = Object.keys(REGIME_TIERS);
const REGIME_FILTER_KEY = 'intraday_ml_selected_regimes';

// ── Persistent Algorithm Filter (localStorage) ─────────────────────────────

const ALGO_FILTER_KEY = 'intraday_ml_selected_algos';
const isAdmin = typeof localStorage !== 'undefined' && localStorage.getItem('intraday_ml_admin') === 'true';
const ALL_ALGO_KEYS = Object.keys(ALGO_CONFIG).filter(k => !ALGO_CONFIG[k].hidden || isAdmin);
const DEFAULT_ALGO_KEYS = Object.keys(ALGO_CONFIG).filter(k => !ALGO_CONFIG[k].hidden);

function loadSelectedAlgos() {
  try {
    const saved = localStorage.getItem(ALGO_FILTER_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const valid = parsed.filter(k => ALL_ALGO_KEYS.includes(k));
      return valid.length > 0 ? valid : null;
    }
  } catch {}
  return null;
}

function saveSelectedAlgos(selected) {
  try { localStorage.setItem(ALGO_FILTER_KEY, JSON.stringify(selected)); } catch {}
}

function useAlgoFilter() {
  const [selected, setSelected] = useState(() => {
    return loadSelectedAlgos() || DEFAULT_ALGO_KEYS;
  });

  const toggle = useCallback((algoKey) => {
    setSelected(prev => {
      let next;
      if (prev.includes(algoKey)) {
        if (prev.length <= 1) return prev;
        next = prev.filter(k => k !== algoKey);
      } else {
        next = [...prev, algoKey];
      }
      saveSelectedAlgos(next);
      return next;
    });
  }, []);

  return { selected, toggle };
}

// ── Persistent Extended Hours Filter (localStorage) ─────────────────────────

const EXT_HOURS_KEY = 'intraday_ml_show_extended';

function useExtendedHoursFilter() {
  const [showExtended, setShowExtended] = useState(() => {
    try { return localStorage.getItem(EXT_HOURS_KEY) !== 'false'; } catch { return true; }
  });
  const toggleExtended = useCallback(() => {
    setShowExtended(prev => {
      const next = !prev;
      try { localStorage.setItem(EXT_HOURS_KEY, String(next)); } catch {}
      return next;
    });
  }, []);
  return { showExtended, toggleExtended };
}

// ── Persistent Regime Tier Filter (localStorage) ────────────────────────────

function useRegimeFilter() {
  const [selected, setSelected] = useState(() => {
    try {
      const saved = localStorage.getItem(REGIME_FILTER_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const valid = parsed.filter(k => ALL_REGIME_KEYS.includes(k));
        return valid.length > 0 ? valid : ALL_REGIME_KEYS;
      }
    } catch {}
    return ALL_REGIME_KEYS;
  });

  const toggle = useCallback((tierKey) => {
    setSelected(prev => {
      let next;
      if (prev.includes(tierKey)) {
        if (prev.length <= 1) return prev;
        next = prev.filter(k => k !== tierKey);
      } else {
        next = [...prev, tierKey];
      }
      try { localStorage.setItem(REGIME_FILTER_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  return { selected, toggle };
}

// ── Persistent Panel State (localStorage) ───────────────────────────────────

const PANEL_OPEN_KEY = 'intraday_ml_panel_open';

function usePanelState() {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(PANEL_OPEN_KEY);
      return saved === null ? true : saved === 'true';
    } catch { return true; }
  });

  const toggle = useCallback(() => {
    setOpen(prev => {
      const next = !prev;
      try { localStorage.setItem(PANEL_OPEN_KEY, String(next)); } catch {}
      return next;
    });
  }, []);

  return { open, toggle };
}

// ── Extended Hours Detection ────────────────────────────────────────────────

function isExtendedHours(utcDateStr) {
  if (!utcDateStr) return false;
  const d = new Date(utcDateStr + (utcDateStr.endsWith('Z') ? '' : 'Z'));
  const etStr = d.toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: 'numeric', hour12: false });
  const [h, m] = etStr.split(':').map(Number);
  if (h < 9 || (h === 9 && m < 30) || h >= 16) return true;
  return false;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function getTimezoneAbbr() {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(new Date());
    const tz = parts.find(p => p.type === 'timeZoneName');
    return tz ? tz.value : '';
  } catch { return ''; }
}

function getUserTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
  catch { return 'UTC'; }
}

function formatTime(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true });
}

function AlgoBadge({ version, timeframe, regimeTier }) {
  let baseAlgo = version;
  let tier = regimeTier;
  if (version.startsWith('v2c_')) { baseAlgo = 'v2c'; tier = tier || version.substring(4); }
  else if (version.startsWith('v2_')) { baseAlgo = 'v2'; tier = tier || version.substring(3); }
  const cfg = ALGO_CONFIG[baseAlgo] || ALGO_CONFIG.v1c;
  const tfLabel = timeframe === '1h' ? ' 1H' : ' 5m';
  const tierLabel = tier && REGIME_TIERS[tier] ? ` [${REGIME_TIERS[tier].short}]` : '';
  return <span className={`badge badge-algo-lg ${cfg.cssClass}`}>{cfg.label}{tfLabel}{tierLabel}</span>;
}

// ── Section 1: Active Opportunities ─────────────────────────────────────────

function buildOpportunities(statuses, selectedAlgos, selectedRegimes) {
  const opps = [];
  for (const [ticker, data] of Object.entries(statuses)) {
    if (selectedAlgos.includes('v1c')) {
      const v1cModels = data.models_v1c || {};
      for (const [tf, model] of Object.entries(v1cModels)) {
        opps.push({ ticker, timeframe: tf, version: 'v1c', model, livePrice: data.live_price });
      }
    }
    const tierOrder = ['strict', 'firm', 'moderate', 'relaxed', 'none'];
    for (const [algoKey, regimeData, version] of [
      ['v2c', data.v2c_regimes || {}, 'v2c'],
    ]) {
      if (!selectedAlgos.includes(algoKey)) continue;
      for (const [tf, tiers] of Object.entries(regimeData)) {
        let best = null;
        for (const tierName of tierOrder) {
          const model = tiers[tierName];
          if (!model || !selectedRegimes.includes(tierName)) continue;
          if (model.in_position || model.recommendation === 'BUY') {
            best = { ticker, timeframe: tf, version, regime_tier: tierName, model, livePrice: data.live_price };
            break;
          }
        }
        if (best) opps.push(best);
      }
    }
  }
  const dedupKey = (o) => `${o.ticker}_${o.timeframe}_${o.model?.entry_price || ''}`;
  const seen = {};
  const dedupedOpps = [];
  for (const o of opps) {
    const key = dedupKey(o);
    const entryPrice = o.model?.entry_price;
    if (entryPrice && seen[key] !== undefined) {
      if (o.version === 'v2c' && dedupedOpps[seen[key]].version === 'v1c') {
        dedupedOpps[seen[key]] = o;
      }
    } else {
      seen[key] = dedupedOpps.length;
      dedupedOpps.push(o);
    }
  }

  // Filter out invalid signals: price below stop = impossible BUY
  const validOpps = dedupedOpps.filter(o => {
    if (!o.model) return false;
    const sl = Number(o.model.stop_loss || 0);
    const p = Number(o.model.price || 0);
    if (sl > 0 && p > 0 && p < sl && !o.model.in_position) return false; // invalid signal
    return true;
  });

  const priority = { open: 0, BUY: 1, WAIT: 2, NO_TRADE: 3, ERROR: 4, NO_DATA: 4 };
  validOpps.sort((a, b) => {
    const aOpen = a.model?.in_position ? 0 : 1;
    const bOpen = b.model?.in_position ? 0 : 1;
    if (aOpen !== bOpen) return aOpen - bOpen;
    const aPri = priority[a.model?.recommendation] ?? 3;
    const bPri = priority[b.model?.recommendation] ?? 3;
    return aPri - bPri;
  });
  return validOpps;
}

function OpportunityCard({ opp, historicalStats, onClick }) {
  const { ticker, timeframe, version, model, regime_tier } = opp;
  if (!model) return null;
  const tierInfo = regime_tier ? REGIME_TIERS[regime_tier] : null;

  const nowET = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: 'numeric', hour12: false });
  const [nowH, nowM] = nowET.split(':').map(Number);
  const isExtHours = nowH < 9 || (nowH === 9 && nowM < 30) || nowH >= 16;

  const rec = (model.recommendation || 'NO_TRADE');
  const isOpen = model.in_position;
  const isBuy = rec === 'BUY';
  const isWait = rec === 'WAIT';

  const entryPrice = Number(model.entry_price || 0);
  const livePrice = opp.livePrice ? Number(opp.livePrice) : null;
  const currentPrice = livePrice || Number(model.price || 0);
  const unrealizedPnl = isOpen && entryPrice > 0
    ? ((currentPrice - entryPrice) / entryPrice * 100) : 0;

  let holdingStr = '';
  if (isOpen && model.entry_time) {
    const mins = Math.round((Date.now() - new Date(model.entry_time).getTime()) / 60000);
    holdingStr = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
  }

  const borderColor = isOpen ? '#22c55e' : isBuy ? '#22c55e' : isWait ? '#fbbf24' : '#333';

  return (
    <div className="opp-card" style={{ borderLeftColor: borderColor, cursor: 'pointer' }} onClick={onClick}>
      <div className="opp-ticker-col">
        <div className="opp-ticker">{ticker}</div>
      </div>
      <div className="opp-details">
        <div className="opp-detail">
          <Tooltip id="signal"><span className="opp-label">Signal</span></Tooltip>
          <span className={`opp-value ${(isOpen || isBuy) ? 'buy' : isWait ? 'wait' : ''}`}>
            {isOpen ? 'BUY' : rec.replace('_', ' ')}
          </span>
        </div>
        {isOpen ? (
          <>
            <div className="opp-detail">
              <span className="opp-label">Entry</span>
              <span className="opp-value">${entryPrice.toFixed(2)}</span>
            </div>
            <div className="opp-detail">
              <span className="opp-label">Current</span>
              <span className="opp-value">${currentPrice.toFixed(2)}</span>
            </div>
            <div className="opp-detail">
              <span className="opp-label">Unrealized</span>
              <span className={`opp-value ${unrealizedPnl >= 0 ? 'positive' : 'negative'}`}>
                {unrealizedPnl >= 0 ? '+' : ''}{unrealizedPnl.toFixed(2)}%
              </span>
            </div>
            {holdingStr && (
              <div className="opp-detail">
                <span className="opp-label">Holding</span>
                <span className="opp-value">{holdingStr}</span>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="opp-detail">
              <span className="opp-label">Price</span>
              <span className="opp-value">${currentPrice.toFixed(2)}</span>
            </div>
          </>
        )}
        {model.stop_loss && (isBuy || isOpen) && (
          <>
            <div className="opp-detail">
              <span className="opp-label">Stop</span>
              <span className="opp-value">
                ${Number(model.stop_loss).toFixed(2)}
                {model.algorithm_version?.startsWith('v2')
                  ? ` | Trailing ${Number(model.stop_pct || 0).toFixed(2)}%`
                  : ` (-${Number(model.stop_pct || 0).toFixed(2)}%) | absolute`}
              </span>
            </div>
            <div className="opp-detail">
              <span className="opp-label">Target</span>
              <span className="opp-value">${Number(model.take_profit).toFixed(2)}</span>
            </div>
          </>
        )}
        <div className="opp-detail">
          <Tooltip id="confidence"><span className="opp-label">Confidence</span></Tooltip>
          <span className="opp-value">{((model.confidence || 0) * 100).toFixed(0)}%</span>
        </div>
      </div>
      <div className="opp-badges">
        {isOpen
          ? <span className="badge badge-status open">OPEN</span>
          : isBuy
            ? <span className="badge badge-status buy">BUY</span>
            : isWait
              ? <span className="badge badge-status wait">WAIT</span>
              : <span className="badge badge-status no-trade">NO TRADE</span>
        }
        <AlgoBadge version={version} timeframe={timeframe} regimeTier={regime_tier} />
        {isExtHours && <span className="ext-badge">EXT HOURS</span>}
        {model.price_stale && <span className="warning-badge stale">STALE DATA</span>}
        {model.stop_breached && <span className="warning-badge breach">STOP BREACHED</span>}
        {model.signal_invalid && !isOpen && <span className="warning-badge invalid">INVALID SIGNAL</span>}
      </div>
      {historicalStats && historicalStats.trades > 0 && (
        <div className="opp-historical">
          Historical: {historicalStats.winRate}% WR | {historicalStats.profitFactor} PF | {historicalStats.trades} trades
        </div>
      )}
    </div>
  );
}

function getDataAge(updatedAt) {
  if (!updatedAt) return null;
  const ms = Date.now() - new Date(updatedAt).getTime();
  return Math.round(ms / 60000);
}

// ── Algorithm Stats (kept for reuse) ────────────────────────────────────────

function computeAlgoStats(trades) {
  const closed = trades.filter(t => t.status === 'closed' && t.pnl_pct != null);
  const wins = closed.filter(t => Number(t.pnl_pct) > 0);
  const losses = closed.filter(t => Number(t.pnl_pct) <= 0);
  const totalWin = wins.reduce((s, t) => s + Number(t.pnl_pct), 0);
  const totalLoss = Math.abs(losses.reduce((s, t) => s + Number(t.pnl_pct), 0));

  let cagr = '\u2014';
  if (closed.length >= 2) {
    const sorted = [...closed].sort((a, b) => new Date(a.entry_datetime) - new Date(b.entry_datetime));
    const equity = sorted.reduce((eq, t) => eq * (1 + Number(t.pnl_pct) / 100), 1.0);
    const first = new Date(sorted[0].entry_datetime);
    const last = new Date(sorted[sorted.length - 1].exit_datetime || sorted[sorted.length - 1].entry_datetime);
    const years = (last - first) / (365.25 * 24 * 60 * 60 * 1000);
    if (years >= 0.25 && equity > 0) {
      cagr = ((Math.pow(equity, 1 / years) - 1) * 100).toFixed(1);
    } else if (years > 0.01) {
      cagr = ((equity - 1) * 100).toFixed(1) + ' (total)';
    }
  }

  return {
    trades: closed.length,
    winRate: closed.length > 0 ? (wins.length / closed.length * 100).toFixed(1) : '0',
    profitFactor: totalLoss > 0 ? (totalWin / totalLoss).toFixed(2) : '\u221E',
    avgPnl: closed.length > 0 ? (closed.reduce((s, t) => s + Number(t.pnl_pct), 0) / closed.length).toFixed(3) : '0',
    cagr,
  };
}

// ── Algorithm Detail Modal ──────────────────────────────────────────────────

function AlgoDetailModal({ modalAlgo, histStatsMap, transactions, onClose }) {
  if (!modalAlgo) return null;

  const { ticker, version, regime_tier, timeframe } = modalAlgo;
  const modelVer = regime_tier ? `${version}_${regime_tier}` : version;
  const histKey = `${modelVer}_${ticker}`;
  const stats = histStatsMap[histKey];

  // Get all transactions for this algo+ticker
  const allTxns = useMemo(() => {
    const combined = [];
    for (const [, data] of Object.entries(transactions || {})) {
      if (data?.transactions) combined.push(...data.transactions);
    }
    return combined;
  }, [transactions]);

  const recentTrades = useMemo(() => {
    return allTxns
      .filter(t => {
        const ver = t.model_version || 'v1';
        if (regime_tier) {
          return ver === `${version}_${regime_tier}` && t.ticker === ticker;
        }
        return ver === version && t.ticker === ticker;
      })
      .filter(t => t.status === 'closed')
      .sort((a, b) => (b.exit_datetime || '').localeCompare(a.exit_datetime || ''))
      .slice(0, 5);
  }, [allTxns, ticker, version, regime_tier]);

  const tierInfo = regime_tier ? REGIME_TIERS[regime_tier] : null;

  return (
    <div className="algo-modal-backdrop" onClick={onClose}>
      <div className="algo-modal" onClick={(e) => e.stopPropagation()}>
        <div className="algo-modal-header">
          <div className="algo-modal-title-row">
            <span className="algo-modal-ticker">{ticker}</span>
            <AlgoBadge version={version} timeframe={timeframe} regimeTier={regime_tier} />
          </div>
          <button className="algo-modal-close" onClick={onClose}>&times;</button>
        </div>

        {stats && stats.trades > 0 ? (
          <>
            <div className="algo-modal-stats-grid">
              <div className="algo-modal-stat">
                <div className="algo-modal-stat-label">Win Rate</div>
                <div className={`algo-modal-stat-value ${Number(stats.winRate) >= 50 ? 'positive' : 'negative'}`}>
                  {stats.winRate}%
                </div>
              </div>
              <div className="algo-modal-stat">
                <div className="algo-modal-stat-label">Profit Factor</div>
                <div className="algo-modal-stat-value positive">{stats.profitFactor}</div>
              </div>
              <div className="algo-modal-stat">
                <div className="algo-modal-stat-label">Avg PnL</div>
                <div className={`algo-modal-stat-value ${Number(stats.avgPnl) >= 0 ? 'positive' : 'negative'}`}>
                  {Number(stats.avgPnl) >= 0 ? '+' : ''}{stats.avgPnl}%
                </div>
              </div>
              <div className="algo-modal-stat">
                <div className="algo-modal-stat-label">{String(stats.cagr).includes('total') ? 'Return' : 'CAGR'}</div>
                <div className={`algo-modal-stat-value ${stats.cagr !== '\u2014' && parseFloat(stats.cagr) >= 0 ? 'positive' : stats.cagr !== '\u2014' ? 'negative' : ''}`}>
                  {stats.cagr !== '\u2014' ? `${parseFloat(stats.cagr) >= 0 ? '+' : ''}${stats.cagr}${String(stats.cagr).includes('total') ? '' : '%'}` : '\u2014'}
                </div>
              </div>
            </div>

            <div className="algo-modal-meta">
              <span>{stats.trades} closed trades</span>
              {tierInfo && <span className="algo-modal-regime">Regime: {tierInfo.label} — {tierInfo.desc}</span>}
            </div>
          </>
        ) : (
          <div className="algo-modal-empty">
            No historical trade data for this algorithm + ticker combination.
            {tierInfo && <div className="algo-modal-regime" style={{ marginTop: 8 }}>Regime: {tierInfo.label} — {tierInfo.desc}</div>}
          </div>
        )}

        {recentTrades.length > 0 && (
          <div className="algo-modal-recent">
            <div className="algo-modal-recent-title">Last 5 Trades</div>
            <table className="algo-modal-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Entry</th>
                  <th>Exit</th>
                  <th>P&L</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {recentTrades.map((t, i) => {
                  const pnl = Number(t.pnl_pct || 0);
                  return (
                    <tr key={t.id || i}>
                      <td>{formatDate(t.entry_datetime)}</td>
                      <td>${Number(t.entry_price).toFixed(2)}</td>
                      <td>${Number(t.exit_price).toFixed(2)}</td>
                      <td className={pnl > 0 ? 'positive' : pnl < 0 ? 'negative' : ''}>
                        {pnl > 0 ? '+' : ''}{pnl.toFixed(2)}%
                      </td>
                      <td>
                        <span className={`exit-badge ${t.exit_type?.includes('profit') || t.exit_type?.includes('trail') ? 'win' : 'loss'}`}>
                          {(t.exit_type || '').replace(/_/g, ' ')}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Signals Section (replaces ActiveOpportunities in main flow) ─────────────

function SignalCards({ opps, histStatsMap, onCardClick, marketStatus, marketOpen, showExtended }) {
  const [showAll, setShowAll] = useState(false);

  let newestUpdate = null;
  for (const opp of opps) {
    const updatedAt = opp.model?.updated_at;
    if (updatedAt && (!newestUpdate || updatedAt > newestUpdate)) {
      newestUpdate = updatedAt;
    }
  }
  const dataAgeMin = newestUpdate ? getDataAge(newestUpdate) : null;
  const isStale = dataAgeMin !== null && dataAgeMin > 15;

  const extendedHours = marketStatus?.extended_hours ?? false;

  const activeOpps = opps.filter(o => o.model?.in_position || o.model?.recommendation === 'BUY');
  const hiddenCount = opps.length - activeOpps.length;
  const display = showAll ? opps : activeOpps;

  return (
    <div className="section">
      <h2>Signals</h2>
      <p className="section-subtitle">Live signals across all algorithms and tickers. Click a card for algo details.</p>

      {!marketOpen && !extendedHours && (
        <div className="market-closed-banner">
          Market is closed. Signals below are from the last session.
        </div>
      )}
      {!marketOpen && extendedHours && (
        <div className="market-closed-banner" style={{ borderColor: 'rgba(251,191,36,0.4)', color: '#fbbf24' }}>
          Market is currently in extended hours. Prices update every 30 seconds.
        </div>
      )}
      {marketOpen && isStale && (
        <div className="market-closed-banner" style={{ borderColor: 'rgba(239,68,68,0.4)', color: '#f87171' }}>
          Live scorer hasn't updated in {dataAgeMin >= 60 ? `${Math.round(dataAgeMin/60)}h ${dataAgeMin%60}m` : `${dataAgeMin}m`}. It may not be running.
        </div>
      )}

      {display.length === 0 && (
        <p className="no-opps">No active opportunities right now. All models show WAIT or NO_TRADE.</p>
      )}

      <div className={`opp-grid ${!marketOpen && !opps.some(o => o.model?.in_position) ? 'stale' : ''}`}>
        {display.map((opp, i) => {
          const modelVer = opp.regime_tier ? `${opp.version}_${opp.regime_tier}` : opp.version;
          const histKey = `${modelVer}_${opp.ticker}`;
          return (
            <OpportunityCard
              key={`${opp.ticker}-${opp.version}-${opp.regime_tier || ''}-${opp.timeframe}`}
              opp={opp}
              historicalStats={histStatsMap[histKey]}
              onClick={() => onCardClick(opp)}
            />
          );
        })}
      </div>
      {hiddenCount > 0 && !showAll && (
        <p className="show-all-link">
          {hiddenCount} algorithm{hiddenCount > 1 ? 's' : ''} showing NO_TRADE (hidden) —{' '}
          <a href="#" onClick={(e) => { e.preventDefault(); setShowAll(true); }}>show all</a>
        </p>
      )}
      {showAll && hiddenCount > 0 && (
        <p className="show-all-link">
          <a href="#" onClick={(e) => { e.preventDefault(); setShowAll(false); }}>hide inactive</a>
        </p>
      )}
    </div>
  );
}

// ── Section: Today's Closed Trades ──────────────────────────────────────────

function TodayTrades({ trades, selectedAlgos, selectedRegimes }) {
  if (!trades || trades.length === 0) return null;

  const filteredTrades = trades.filter(t => {
    const ver = (t.model_version || 'v1');
    if (ver.startsWith('v2c_')) {
      if (!selectedAlgos.includes('v2c')) return false;
      const tierName = ver.substring(4);
      return selectedRegimes.includes(tierName);
    }
    if (ver.startsWith('v2_')) {
      if (!selectedAlgos.includes('v2')) return false;
      const tierName = ver.substring(3);
      return selectedRegimes.includes(tierName);
    }
    return selectedAlgos.includes(ver);
  });

  const staticTrades = filteredTrades.filter(t => (t.trade_style || 'static') === 'static');
  const dynamicTrades = filteredTrades.filter(t => t.trade_style === 'dynamic');

  const computeStats = (list) => {
    const closed = list.filter(t => t.status === 'closed');
    const open = list.filter(t => t.status === 'open');
    const pnl = closed.reduce((s, t) => s + Number(t.pnl_pct || 0), 0);
    const wins = closed.filter(t => Number(t.pnl_pct) > 0).length;
    const losses = closed.filter(t => Number(t.pnl_pct) <= 0).length;
    const wr = closed.length > 0 ? (wins / closed.length * 100).toFixed(0) : 0;
    return { closed, open, pnl, wins, losses, wr, total: list.length };
  };

  const staticStats = computeStats(staticTrades);
  const dynamicStats = computeStats(dynamicTrades);

  const closedTrades = filteredTrades.filter(t => t.status === 'closed');
  const dailyPnl = closedTrades.reduce((s, t) => s + Number(t.pnl_pct || 0), 0);
  const wins = closedTrades.filter(t => Number(t.pnl_pct) > 0).length;
  const losses = closedTrades.filter(t => Number(t.pnl_pct) <= 0).length;
  const wr = closedTrades.length > 0 ? (wins / closedTrades.length * 100).toFixed(0) : 0;

  return (
    <div className="section">
      <h2>Today's Trades</h2>
      <p className="section-subtitle">All trades today across every algorithm and ticker</p>

      {(staticStats.total > 0 || dynamicStats.total > 0) && (
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          {staticStats.total > 0 && (
            <div style={{ flex: 1, minWidth: '200px' }}>
              <h4 style={{ margin: '0 0 0.5rem', color: '#60a5fa' }}>Static Trades</h4>
              <div className="summary-stats">
                <div className="summary-stat">
                  <div className="summary-stat-label">Open</div>
                  <div className="summary-stat-value">{staticStats.open.length}</div>
                </div>
                <div className="summary-stat">
                  <div className="summary-stat-label">Closed</div>
                  <div className="summary-stat-value">{staticStats.closed.length}</div>
                </div>
                <div className="summary-stat">
                  <div className="summary-stat-label">Win Rate</div>
                  <div className="summary-stat-value">{staticStats.wr}%</div>
                </div>
                <div className="summary-stat">
                  <div className="summary-stat-label">P&L</div>
                  <div className={`summary-stat-value ${staticStats.pnl >= 0 ? 'positive' : 'negative'}`}>
                    {staticStats.pnl >= 0 ? '+' : ''}{staticStats.pnl.toFixed(2)}%
                  </div>
                </div>
              </div>
            </div>
          )}
          {dynamicStats.total > 0 && (
            <div style={{ flex: 1, minWidth: '200px' }}>
              <h4 style={{ margin: '0 0 0.5rem', color: '#fbbf24' }}>Dynamic Trades <span className="badge-dynamic" style={{marginLeft: '4px'}}>Signal</span></h4>
              <div className="summary-stats">
                <div className="summary-stat">
                  <div className="summary-stat-label">Open</div>
                  <div className="summary-stat-value">{dynamicStats.open.length}</div>
                </div>
                <div className="summary-stat">
                  <div className="summary-stat-label">Closed</div>
                  <div className="summary-stat-value">{dynamicStats.closed.length}</div>
                </div>
                <div className="summary-stat">
                  <div className="summary-stat-label">Win Rate</div>
                  <div className="summary-stat-value">{dynamicStats.wr}%</div>
                </div>
                <div className="summary-stat">
                  <div className="summary-stat-label">P&L</div>
                  <div className={`summary-stat-value ${dynamicStats.pnl >= 0 ? 'positive' : 'negative'}`}>
                    {dynamicStats.pnl >= 0 ? '+' : ''}{dynamicStats.pnl.toFixed(2)}%
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {closedTrades.length > 0 && (
        <div className="summary-stats" style={{ marginBottom: '0.5rem', opacity: 0.7 }}>
          <div className="summary-stat">
            <div className="summary-stat-label">Total Trades</div>
            <div className="summary-stat-value">{closedTrades.length}</div>
          </div>
          <div className="summary-stat">
            <div className="summary-stat-label">Won</div>
            <div className="summary-stat-value positive">{wins}</div>
          </div>
          <div className="summary-stat">
            <div className="summary-stat-label">Lost</div>
            <div className="summary-stat-value negative">{losses}</div>
          </div>
          <div className="summary-stat">
            <div className="summary-stat-label">Win Rate</div>
            <div className="summary-stat-value">{wr}%</div>
          </div>
          <div className="summary-stat">
            <div className="summary-stat-label">Combined P&L</div>
            <div className={`summary-stat-value ${dailyPnl >= 0 ? 'positive' : 'negative'}`}>
              {dailyPnl >= 0 ? '+' : ''}{dailyPnl.toFixed(2)}%
            </div>
          </div>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table className="txn-table">
          <thead>
            <tr>
              <th>Ticker</th>
              <th>Algo</th>
              <th>Style</th>
              <th>Session</th>
              <th>Entry</th>
              <th>Exit</th>
              <th>P&L</th>
              <th>Exit Type</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            {filteredTrades.map((t, idx) => {
              const isOpen = t.status === 'open';
              const pnl = Number(t.pnl_pct || 0);
              const version = t.model_version || 'v1';
              return (
                <tr key={t.id || idx} className={isOpen ? 'open-trade-row' : ''}>
                  <td><strong>{t.ticker}</strong></td>
                  <td>
                    <AlgoBadge version={version} timeframe={t.timeframe} />
                  </td>
                  <td>
                    <span className={`badge ${(t.trade_style || 'static') === 'dynamic' ? 'badge-dynamic' : 'badge-static'}`}>
                      {(t.trade_style || 'static') === 'dynamic' ? 'Dynamic' : 'Static'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${(t.session_mode || 'regular') === 'extended' ? 'badge-ext' : 'badge-reg'}`}>
                      {(t.session_mode || 'regular') === 'extended' ? 'EXT' : 'REG'}
                    </span>
                  </td>
                  <td>
                    ${Number(t.entry_price).toFixed(2)}{' '}
                    <span style={{ color: '#666', fontSize: '0.7rem' }}>{formatTime(t.entry_datetime)}</span>
                  </td>
                  <td>
                    {isOpen ? (
                      <span className="status-open"><span className="status-open-dot"></span>OPEN</span>
                    ) : `$${Number(t.exit_price).toFixed(2)}`}
                  </td>
                  <td className={isOpen ? '' : pnl > 0 ? 'positive' : pnl < 0 ? 'negative' : ''}>
                    {isOpen ? '-' : `${pnl > 0 ? '+' : ''}${pnl.toFixed(2)}%`}
                  </td>
                  <td>
                    {isOpen ? '-' : (
                      <span className={`exit-badge ${t.exit_type?.includes('profit') || t.exit_type?.includes('trail') ? 'win' : 'loss'}`}>
                        {(t.exit_type || '').replace(/_/g, ' ')}
                      </span>
                    )}
                  </td>
                  <td>{t.holding_bars ? `${t.holding_bars} bars` : '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Section: Transaction History ─────────────────────────────────────────────

function FeatureJustification({ features }) {
  if (!features || Object.keys(features).length === 0) return null;
  return (
    <div className="justification-panel">
      <div className="justification-title">Key Features</div>
      <div className="justification-features">
        {Object.entries(features).map(([name, data]) => (
          <div key={name} className="justification-item">
            <span className="feat-name">{name.replace(/_/g, ' ')}</span>
            <span className="feat-value">{typeof data === 'object' ? data.value : data}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TransactionHistory({ transactions, selectedAlgos, selectedRegimes, showExtended, tradeMode }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filterAlgo = searchParams.get('algo') || 'All';
  const filterTicker = searchParams.get('ticker') || 'All';
  const filterTf = searchParams.get('tf') || 'All';
  const filterYear = searchParams.get('year') || 'All';
  const filterSource = searchParams.get('source') || 'All';

  const setFilter = useCallback((key, value) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value === 'All') next.delete(key);
      else next.set(key, value);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setFilterAlgo = useCallback(v => setFilter('algo', v), [setFilter]);
  const setFilterTicker = useCallback(v => setFilter('ticker', v), [setFilter]);
  const setFilterTf = useCallback(v => setFilter('tf', v), [setFilter]);
  const setFilterYear = useCallback(v => setFilter('year', v), [setFilter]);
  const setFilterSource = useCallback(v => setFilter('source', v), [setFilter]);

  const [page, setPage] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);
  const PAGE_SIZE = 20;

  useEffect(() => { setPage(0); setExpandedRow(null); },
    [filterAlgo, filterTicker, filterTf, filterYear, filterSource]);

  const allTxns = useMemo(() => {
    const combined = [];
    for (const [key, data] of Object.entries(transactions)) {
      if (data?.transactions) {
        combined.push(...data.transactions);
      }
    }
    const seen = new Set();
    const unique = [];
    for (const t of combined) {
      const id = t.id || `${t.entry_datetime}_${t.entry_price}`;
      if (!seen.has(id)) {
        seen.add(id);
        unique.push(t);
      }
    }
    unique.sort((a, b) => {
      if (a.status === 'open' && b.status !== 'open') return -1;
      if (b.status === 'open' && a.status !== 'open') return 1;
      return (b.entry_datetime || '').localeCompare(a.entry_datetime || '');
    });
    return unique;
  }, [transactions]);

  const filtered = useMemo(() => {
    return allTxns.filter(t => {
      const style = t.trade_style || 'static';
      if (tradeMode && style !== tradeMode) return false;
      const ver = t.model_version || 'v1';
      if (ver.startsWith('v2c_')) {
        if (!selectedAlgos.includes('v2c')) return false;
        const tierName = ver.substring(4);
        if (!selectedRegimes.includes(tierName)) return false;
      } else if (ver.startsWith('v2_')) {
        if (!selectedAlgos.includes('v2')) return false;
        const tierName = ver.substring(3);
        if (!selectedRegimes.includes(tierName)) return false;
      } else if (ver === 'v2c' || ver === 'v2') {
        const algoKey = ver.startsWith('v2c') ? 'v2c' : 'v2';
        if (!selectedAlgos.includes(algoKey)) return false;
        if (!selectedRegimes.includes('none')) return false;
      } else if (!selectedAlgos.includes(ver)) return false;
      if (filterAlgo !== 'All') {
        const fa = filterAlgo.toLowerCase();
        if (!ver.startsWith(fa)) return false;
      }
      if (filterTicker !== 'All' && t.ticker !== filterTicker) return false;
      if (filterTf !== 'All') {
        if (filterTf === '5M' && t.timeframe !== '5min') return false;
        if (filterTf === '1H' && t.timeframe !== '1h') return false;
      }
      if (filterYear !== 'All' && t.entry_datetime && !t.entry_datetime.startsWith(filterYear)) return false;
      if (filterSource !== 'All') {
        if (filterSource === 'Live' && t.source !== 'live') return false;
        if (filterSource === 'Backtest' && t.source !== 'backtest') return false;
      }
      if (!showExtended && t.timeframe !== '1h' && isExtendedHours(t.entry_datetime)) return false;
      return true;
    });
  }, [allTxns, selectedAlgos, selectedRegimes, filterAlgo, filterTicker, filterTf, filterYear, filterSource, showExtended, tradeMode]);

  const stats = useMemo(() => {
    const wins = filtered.filter(t => Number(t.pnl_pct) > 0);
    const losses = filtered.filter(t => Number(t.pnl_pct) <= 0 && t.status === 'closed');
    const totalPnl = filtered.reduce((s, t) => s + Number(t.pnl_pct || 0), 0);
    const avgWin = wins.length > 0 ? wins.reduce((s, t) => s + Number(t.pnl_pct), 0) / wins.length : 0;
    const avgLoss = losses.length > 0 ? losses.reduce((s, t) => s + Number(t.pnl_pct), 0) / losses.length : 0;
    const closedCount = filtered.filter(t => t.status === 'closed').length;
    const closedTrades = filtered.filter(t => t.status === 'closed');
    const totalPnlSum = closedTrades.reduce((s, t) => s + Number(t.pnl_pct || 0), 0);
    const dates = closedTrades.map(t => new Date(t.entry_datetime)).filter(d => !isNaN(d));
    const minDate = dates.length > 0 ? new Date(Math.min(...dates)) : null;
    const maxDate = dates.length > 0 ? new Date(Math.max(...dates)) : null;
    const years = minDate && maxDate ? (maxDate - minDate) / (365.25 * 86400000) : 1;
    const cagr = years > 0 ? totalPnlSum / years : 0;
    return {
      total: filtered.length,
      winRate: closedCount > 0 ? (wins.length / closedCount * 100).toFixed(1) : '0',
      pf: Math.abs(avgLoss) > 0 ? ((avgWin * wins.length) / (Math.abs(avgLoss) * losses.length)).toFixed(2) : 'N/A',
      avgWin: avgWin.toFixed(2),
      avgLoss: avgLoss.toFixed(2),
      cagr,
    };
  }, [filtered]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const display = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const FilterGroup = ({ label, options, value, onChange }) => (
    <div className="filter-group">
      <span className="filter-group-label">{label}</span>
      {options.map(opt => (
        <button key={opt} className={`filter-btn ${value === opt ? 'active' : ''}`}
          onClick={() => onChange(opt)}>{opt}</button>
      ))}
    </div>
  );

  return (
    <div className="section">
      <h2>Transaction History</h2>
      <p className="section-subtitle">All backtest + live trades, filterable by algorithm, ticker, timeframe, year</p>

      <div className="filters-bar">
        <FilterGroup label="Algo" options={['All', ...Object.entries(ALGO_CONFIG).filter(([_, cfg]) => !cfg.hidden || isAdmin).map(([k]) => k.toUpperCase())]} value={filterAlgo} onChange={setFilterAlgo} />
        <div className="filter-sep" />
        <FilterGroup label="Ticker" options={['All', 'TQQQ', 'UPRO', 'SQQQ', 'SPXU']} value={filterTicker} onChange={setFilterTicker} />
        <div className="filter-sep" />
        <FilterGroup label="TF" options={['All', '5M', '1H']} value={filterTf} onChange={setFilterTf} />
        <div className="filter-sep" />
        <FilterGroup label="Year" options={YEARS} value={filterYear} onChange={setFilterYear} />
        <div className="filter-sep" />
        <FilterGroup label="Source" options={['All', 'Live', 'Backtest']} value={filterSource} onChange={setFilterSource} />
      </div>

      <div className="summary-stats">
        <div className="summary-stat">
          <div className="summary-stat-label">Trades</div>
          <div className="summary-stat-value">{stats.total}</div>
        </div>
        <div className="summary-stat">
          <div className="summary-stat-label">Win Rate</div>
          <div className="summary-stat-value">{stats.winRate}%</div>
        </div>
        <div className="summary-stat">
          <div className="summary-stat-label">Profit Factor</div>
          <div className="summary-stat-value positive">{stats.pf}</div>
        </div>
        <div className="summary-stat">
          <div className="summary-stat-label">Avg Win</div>
          <div className="summary-stat-value positive">+{stats.avgWin}%</div>
        </div>
        <div className="summary-stat">
          <div className="summary-stat-label">Avg Loss</div>
          <div className="summary-stat-value negative">{stats.avgLoss}%</div>
        </div>
        <div className="summary-stat">
          <div className="summary-stat-label">Ann. Return</div>
          <div className={`summary-stat-value ${stats.cagr >= 0 ? 'positive' : 'negative'}`}>
            {stats.cagr >= 0 ? '+' : ''}{stats.cagr.toFixed(0)}%
          </div>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="txn-table">
          <thead>
            <tr>
              <th></th>
              <th>Ticker</th>
              <th>Algo</th>
              <th>Source</th>
              <th>Entry</th>
              <th>Exit</th>
              <th>Entry $</th>
              <th>Exit $</th>
              <th>P&L</th>
              <th>Exit Type</th>
              <th>Bars</th>
            </tr>
          </thead>
          <tbody>
            {display.map((t, idx) => {
              const globalIdx = page * PAGE_SIZE + idx;
              const isExpanded = expandedRow === globalIdx;
              const kf = typeof t.key_features === 'string' ? JSON.parse(t.key_features) : t.key_features;
              const hasFeatures = kf && Object.keys(kf).length > 0;
              const isOpen = t.status === 'open';
              const pnl = Number(t.pnl_pct || 0);
              const version = t.model_version || 'v1';
              return (
                <React.Fragment key={t.id || idx}>
                  <tr className={`${isExpanded ? 'expanded-row' : ''} ${isOpen ? 'open-trade-row' : ''}`}
                    style={{ cursor: hasFeatures ? 'pointer' : 'default' }}
                    onClick={() => hasFeatures && setExpandedRow(isExpanded ? null : globalIdx)}>
                    <td style={{ width: 24, textAlign: 'center', color: '#555' }}>
                      {hasFeatures ? (isExpanded ? '\u25BC' : '\u25B6') : ''}
                    </td>
                    <td><strong>{t.ticker}</strong></td>
                    <td>
                      <AlgoBadge version={version} timeframe={t.timeframe} />
                    </td>
                    <td>
                      <span className={`source-badge ${t.source === 'live' ? 'live' : 'bt'}`}>
                        {t.source === 'live' ? 'LIVE' : 'BT'}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(t.entry_datetime)}
                      {isExtendedHours(t.entry_datetime) && <span className="ext-badge">EXT</span>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {isOpen ? (
                        <span className="status-open"><span className="status-open-dot"></span>OPEN</span>
                      ) : formatDateTime(t.exit_datetime)}
                    </td>
                    <td>${Number(t.entry_price).toFixed(2)}</td>
                    <td>{isOpen ? '-' : `$${Number(t.exit_price).toFixed(2)}`}</td>
                    <td className={isOpen ? '' : pnl > 0 ? 'positive' : pnl < 0 ? 'negative' : ''}>
                      {isOpen ? '-' : `${pnl > 0 ? '+' : ''}${pnl.toFixed(2)}%`}
                    </td>
                    <td>
                      {isOpen ? '-' : (
                        <span className={`exit-badge ${t.exit_type?.includes('profit') || t.exit_type?.includes('trail') ? 'win' : 'loss'}`}>
                          {(t.exit_type || '').replace(/_/g, ' ')}
                        </span>
                      )}
                    </td>
                    <td>{t.holding_bars || '-'}</td>
                  </tr>
                  {isExpanded && kf && (
                    <tr className="justification-row">
                      <td colSpan={11}>
                        <FeatureJustification features={kf} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {display.length === 0 && (
              <tr><td colSpan={11} style={{ textAlign: 'center', color: '#555', padding: 24 }}>
                No transactions match the current filters.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button className="page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</button>
          <span className="page-info">{page + 1} / {totalPages}</span>
          <button className="page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}

// ── Info Panel (collapsible, now in sidebar) ─────────────────────────────────

const INFO_PANEL_KEY = 'intraday_ml_info_expanded';

function InfoPanel() {
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem(INFO_PANEL_KEY) === 'true'; } catch { return false; }
  });

  const toggle = () => {
    setExpanded(prev => {
      const next = !prev;
      try { localStorage.setItem(INFO_PANEL_KEY, String(next)); } catch {}
      return next;
    });
  };

  return (
    <div className="panel-section info-panel">
      <button className="info-panel-toggle" onClick={toggle}>
        <span>{expanded ? '\u25BC' : '\u25B6'}</span> About
      </button>
      {expanded && (
        <div className="info-panel-content">
          <h3>Models</h3>
          <ul>
            <li><strong>V1C</strong> — ML ensemble (XGBoost + LightGBM + RF + LR). Trained on pre-2024 data only (no data leakage). Buys when confidence &gt; 52%. No market filter. More signals, lower selectivity.</li>
            <li><strong>V2C</strong> — Same model, higher bar. Buys when confidence &gt; 62% AND market conditions pass. Fewer signals, higher selectivity. Uses trailing stops, R:R 4:1, holds overnight.</li>
          </ul>

          <h3>Market Condition Filters (V2C only)</h3>
          <table className="info-table">
            <thead>
              <tr><th>Filter</th><th>When to Use</th><th>VIX</th><th>RSI (14d)</th><th>Price vs SMA50</th></tr>
            </thead>
            <tbody>
              <tr><td>No Filter</td><td>Maximum signals — use with caution</td><td>Any</td><td>Any</td><td>Any</td></tr>
              <tr><td>Relaxed</td><td>Normal conditions</td><td>&lt; 40</td><td>25 - 85</td><td>Any</td></tr>
              <tr><td>Moderate</td><td>Moderate caution</td><td>&lt; 35</td><td>30 - 80</td><td>Within 15%</td></tr>
              <tr><td>Firm</td><td>Conservative</td><td>&lt; 32</td><td>35 - 78</td><td>Within 10%</td></tr>
              <tr><td>Strict</td><td>Most selective — fewest trades</td><td>&lt; 30</td><td>40 - 75</td><td>Above</td></tr>
            </tbody>
          </table>

          <h3>Timeframes</h3>
          <p><strong>5m</strong> = 5-minute bars (~4-9 trades/wk). <strong>1H</strong> = hourly bars (~2-4 trades/wk, wider stops).</p>

          <p className="info-risk-note">Stricter filters produce fewer but historically more reliable signals. "No Filter" trades in ANY condition including high volatility — check historical stats before relying on it.</p>
        </div>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function IntradayML() {
  const { user, loading: authLoading, subscription, signOut } = useAuth();
  const [showLogin, setShowLogin] = useState(false);

  const isProd = import.meta.env.VITE_MODE === 'prod';
  const showPaywall = isProd && user && !subscription.subscribed;
  const showLoginPrompt = isProd && !user && !authLoading;

  const {
    statuses,
    transactions,
    alerts,
    marketStatus,
    loading,
    error,
    lastUpdated,
    refresh,
    sessionMode,
    changeSessionMode,
  } = useIntradayML();

  const { selected: selectedAlgos, toggle: toggleAlgo } = useAlgoFilter();
  const { selected: selectedRegimes, toggle: toggleRegime } = useRegimeFilter();
  const showExtended = sessionMode === 'extended';
  const { open: panelOpen, toggle: togglePanel } = usePanelState();
  const [selectedTickers, setSelectedTickers] = useState(['TQQQ', 'UPRO', 'SQQQ', 'SPXU']);
  const toggleTicker = (t) => setSelectedTickers(prev =>
    prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]
  );
  const [tradeMode, setTradeMode] = useState('static');
  const [modalAlgo, setModalAlgo] = useState(null);

  // Filter statuses and transactions by selected tickers
  const filteredStatuses = Object.fromEntries(
    Object.entries(statuses).filter(([ticker]) => selectedTickers.includes(ticker))
  );
  const filteredTransactions = Object.fromEntries(
    Object.entries(transactions).filter(([key]) => {
      const ticker = key.split('_')[0];
      return selectedTickers.includes(ticker);
    })
  );

  // Derive Today's Trades from transactions
  const todayTradesFromTxns = useMemo(() => {
    const todayET = new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' });
    const all = [];
    for (const [, data] of Object.entries(filteredTransactions)) {
      for (const t of (data.transactions || [])) {
        const entryET = t.entry_datetime
          ? new Date(t.entry_datetime).toLocaleDateString('en-US', { timeZone: 'America/New_York' })
          : null;
        const exitET = t.exit_datetime
          ? new Date(t.exit_datetime).toLocaleDateString('en-US', { timeZone: 'America/New_York' })
          : null;
        const style = t.trade_style || 'static';
        if (t.source === 'live' && style === tradeMode && (t.status === 'open' || entryET === todayET || exitET === todayET)) {
          all.push(t);
        }
      }
    }
    const seen = new Set();
    return all.filter(t => { if (seen.has(t.id)) return false; seen.add(t.id); return true; })
      .sort((a, b) => {
        if (a.status === 'open' && b.status !== 'open') return -1;
        if (b.status === 'open' && a.status !== 'open') return 1;
        return (b.entry_datetime || '').localeCompare(a.entry_datetime || '');
      });
  }, [filteredTransactions, tradeMode]);

  // Session P&L from today's closed trades
  const sessionPnl = useMemo(() => {
    const closed = todayTradesFromTxns.filter(t => t.status === 'closed');
    if (closed.length === 0) return null;
    return closed.reduce((s, t) => s + Number(t.pnl_pct || 0), 0);
  }, [todayTradesFromTxns]);

  // Build opportunities and hist stats for signal cards
  const allOpps = useMemo(() => buildOpportunities(filteredStatuses, selectedAlgos, selectedRegimes), [filteredStatuses, selectedAlgos, selectedRegimes]);

  const opps = useMemo(() => {
    if (showExtended) return allOpps;
    const nowET = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: 'numeric', hour12: false });
    const [nowH, nowM] = nowET.split(':').map(Number);
    const isExt = nowH < 9 || (nowH === 9 && nowM < 30) || nowH >= 16;
    if (!isExt) return allOpps;
    return [];
  }, [allOpps, showExtended]);

  const histStatsMap = useMemo(() => {
    const allTxns = [];
    for (const [, data] of Object.entries(filteredTransactions || {})) {
      if (data?.transactions) allTxns.push(...data.transactions);
    }
    const groups = {};
    for (const t of allTxns) {
      const ver = t.model_version || 'v1';
      const key = `${ver}_${t.ticker}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    }
    const statsMap = {};
    for (const [key, trades] of Object.entries(groups)) {
      statsMap[key] = computeAlgoStats(trades);
    }
    return statsMap;
  }, [filteredTransactions]);

  const marketOpen = marketStatus?.market_open ?? false;

  // Data staleness for header
  const dataAgeMin = useMemo(() => {
    if (!lastUpdated) return null;
    return Math.round((Date.now() - lastUpdated.getTime()) / 60000);
  }, [lastUpdated]);

  return (
    <div className="intraday-page">
      {/* Header */}
      <header className="intraday-header">
        <div className="header-left">
          <button className="panel-toggle-btn" onClick={togglePanel} title={panelOpen ? 'Collapse panel' : 'Expand panel'}>
            &#9776;
          </button>
          <div>
            <h1>ML Trading</h1>
            <p className="header-subtitle">Multi-algorithm signal dashboard</p>
          </div>
        </div>
        <div className="header-right">
          {sessionPnl !== null && (
            <div className={`header-session-pnl ${sessionPnl >= 0 ? 'positive' : 'negative'}`}>
              Today: {sessionPnl >= 0 ? '+' : ''}{sessionPnl.toFixed(2)}%
            </div>
          )}
          <div className="header-meta">
            <div style={{ color: '#4fc3f7', fontSize: '0.75rem', marginBottom: 2 }}>
              {getTimezoneAbbr()} ({getUserTimezone()})
            </div>
            {lastUpdated && (
              <span className={`header-updated ${dataAgeMin !== null && dataAgeMin > 5 ? 'stale' : ''}`}>
                Updated {dataAgeMin !== null ? `${dataAgeMin}m ago` : lastUpdated.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
              </span>
            )}
          </div>
          {isProd && (
            <div className="header-auth">
              {user ? (
                <button className="auth-btn" onClick={signOut} title={user.email}>
                  Sign Out
                </button>
              ) : (
                <button className="auth-btn auth-btn-primary" onClick={() => setShowLogin(true)}>
                  Sign In
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Auth overlays */}
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
      {showPaywall && <SubscriptionWall />}
      {showLoginPrompt && (
        <div className="login-prompt-overlay" onClick={() => setShowLogin(true)}>
          <div className="login-prompt" onClick={(e) => e.stopPropagation()}>
            <h2>Sign in to access ML Trading</h2>
            <p>Real-time ML-powered trading signals for leveraged ETFs</p>
            <button className="auth-btn auth-btn-primary" onClick={() => setShowLogin(true)}>
              Sign In / Create Account
            </button>
          </div>
        </div>
      )}

      {/* Dashboard Shell: sidebar + main */}
      <div className={`dashboard-shell ${panelOpen ? '' : 'collapsed'}`}>
        {/* Left Control Panel */}
        {panelOpen && (
          <aside className="control-panel">
            {/* Models */}
            <div className="panel-section">
              <div className="panel-section-label">Models</div>
              <div className="panel-filter-group">
                {Object.entries(ALGO_CONFIG).filter(([_, cfg]) => !cfg.hidden || isAdmin).map(([key, cfg]) => (
                  <button
                    key={key}
                    className={`algo-filter-btn ${selectedAlgos.includes(key) ? 'active' : ''} ${cfg.cssClass}`}
                    onClick={() => toggleAlgo(key)}
                    title={key === 'v1c' ? '52% threshold, no market filter' : key === 'v2c' ? '62% threshold + market filter' : ''}
                  >
                    <span className="algo-filter-check">{selectedAlgos.includes(key) ? '\u2713' : ''}</span>
                    {cfg.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Regime Tiers */}
            {(selectedAlgos.includes('v2') || selectedAlgos.includes('v2c')) && (
              <div className="panel-section">
                <div className="panel-section-label" style={{ color: '#818cf8' }}>Market Filter</div>
                <div className="panel-filter-group">
                  {Object.entries(REGIME_TIERS).map(([key, tier]) => (
                    <button
                      key={key}
                      className={`algo-filter-btn regime-btn ${selectedRegimes.includes(key) ? 'active' : ''}`}
                      onClick={() => toggleRegime(key)}
                      title={tier.desc}
                    >
                      <span className="algo-filter-check">{selectedRegimes.includes(key) ? '\u2713' : ''}</span>
                      {tier.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Tickers */}
            <div className="panel-section">
              <div className="panel-section-label">Tickers</div>
              <div className="panel-filter-group">
                {['TQQQ', 'UPRO', 'SQQQ', 'SPXU'].map(t => (
                  <button
                    key={t}
                    className={`algo-filter-btn ${selectedTickers.includes(t) ? 'active' : ''}`}
                    onClick={() => toggleTicker(t)}
                  >
                    <span className="algo-filter-check">{selectedTickers.includes(t) ? '\u2713' : ''}</span>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Session Mode */}
            <div className="panel-section">
              <div className="panel-section-label">Trading Session</div>
              <select
                className="session-mode-select"
                value={sessionMode}
                onChange={(e) => changeSessionMode(e.target.value)}
              >
                <option value="regular">Regular Hours</option>
                <option value="extended">All Sessions</option>
              </select>
              {sessionMode === 'extended' && (
                <div style={{ fontSize: '0.65rem', color: '#fbbf24', marginTop: 4 }}>
                  Includes pre-market, after-hours, overnight
                </div>
              )}
            </div>

            {/* Trade Mode */}
            <div className="panel-section">
              <div className="panel-section-label">Trade Mode</div>
              <div className="panel-filter-group">
                <button
                  className={`algo-filter-btn ${tradeMode === 'static' ? 'active' : ''}`}
                  onClick={() => setTradeMode('static')}
                  style={{ flex: 1 }}
                >
                  Static
                </button>
                <button
                  className={`algo-filter-btn ${tradeMode === 'dynamic' ? 'active' : ''}`}
                  onClick={() => setTradeMode('dynamic')}
                  style={{ flex: 1 }}
                >
                  Dynamic
                </button>
              </div>
            </div>

            {/* Info Panel */}
            <InfoPanel />
          </aside>
        )}

        {/* Main Content */}
        <main className="main-content">
          {/* Error */}
          {error && (
            <div className="error-banner">
              <p>API not available yet. Showing backtest data only.</p>
            </div>
          )}

          {tradeMode === 'dynamic' && (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#888', background: '#1a1a2e', borderRadius: 8, marginBottom: 20 }}>
              <h3 style={{ color: '#f472b6', marginBottom: 8 }}>Dynamic Trades — Coming Soon</h3>
              <p>Dynamic trades exit on signal changes and follow price momentum.</p>
              <p style={{ fontSize: '0.8rem', marginTop: 8 }}>This system is being built separately for maximum reliability.</p>
            </div>
          )}

          {tradeMode === 'static' && (
            <>
              {/* Signals */}
              <SignalCards
                opps={opps}
                histStatsMap={histStatsMap}
                onCardClick={(opp) => setModalAlgo(opp)}
                marketStatus={marketStatus}
                marketOpen={marketOpen}
                showExtended={showExtended}
              />

              {/* Today's Trades */}
              <TodayTrades trades={todayTradesFromTxns} selectedAlgos={selectedAlgos} selectedRegimes={selectedRegimes} />

              {/* Transaction History */}
              <TransactionHistory transactions={filteredTransactions} selectedAlgos={selectedAlgos} selectedRegimes={selectedRegimes} showExtended={showExtended} tradeMode={tradeMode} />
            </>
          )}

          {/* Footer */}
          <footer className="intraday-footer">
            <p>5-model ensemble (XGB+LGBM+RF+LR+SVM) trained on 2016-2025 data with trailing stops</p>
            <p style={{ fontSize: '0.7rem', color: '#555' }}>
              Transactions stored in PostgreSQL for independent verification
            </p>
          </footer>
        </main>
      </div>

      {/* Algorithm Detail Modal */}
      {modalAlgo && (
        <AlgoDetailModal
          modalAlgo={modalAlgo}
          histStatsMap={histStatsMap}
          transactions={filteredTransactions}
          onClose={() => setModalAlgo(null)}
        />
      )}
    </div>
  );
}
